import React, { useCallback, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import WaveSurfer from "wavesurfer.js";
import RecordPlugin from "wavesurfer.js/dist/plugins/record";
import { WavStreamPlayer } from "wavtools";
import { waveBoxSx } from "./styles";

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

const visualizerMicStreams = new Set<MediaStream>();

const stopMediaStreamTracks = (stream: MediaStream | null | undefined): void => {
  if (!(stream instanceof MediaStream)) return;
  stream.getTracks().forEach((track) => {
    try {
      if (track.readyState !== "ended") {
        track.enabled = false;
        track.stop();
      }
    } catch {}
  });
  visualizerMicStreams.delete(stream);
};

export const stopAllVisualizerMicStreams = (): void => {
  visualizerMicStreams.forEach(stopMediaStreamTracks);
  visualizerMicStreams.clear();
};

const releaseRecordPlugin = (recordPlugin: any, wavesurfer: WaveSurfer): void => {
  const stream = recordPlugin?.stream as MediaStream | null | undefined;
  try {
    recordPlugin?.stopRecording?.();
  } catch {}
  stopMediaStreamTracks(stream);
  try {
    recordPlugin?.stopMic?.();
  } catch {}
  try {
    wavesurfer.destroy();
  } catch {}
};

const createSilentWavUrl = (): string => {
  const sampleRate = 8000;
  const seconds = 1;
  const numSamples = sampleRate * seconds;
  const numChannels = 1;
  const bitsPerSample = 16;
  const blockAlign = (numChannels * bitsPerSample) / 8;
  const byteRate = sampleRate * blockAlign;
  const dataSize = numSamples * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++)
      view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeStr(36, "data");
  view.setUint32(40, dataSize, true);
  // data is already zeros
  const blob = new Blob([buffer], { type: "audio/wav" });
  return URL.createObjectURL(blob);
};

// 1) Mic (User) — EXACTLY like WhatsApp recorder WaveSurferVisualizer recording mode
export const WaveSurferMicVisualizer: React.FC<{
  isRecording: boolean;
  height?: number;
  borderRadiusPx?: number;
}> = ({ isRecording, height = 140, borderRadiusPx }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    if (!isRecording) {
      containerRef.current.innerHTML = "";
      return;
    }

    const dummyCanvas = document.createElement("canvas");
    const dummyCtx = dummyCanvas.getContext("2d");
    if (!dummyCtx) return;

    const gradient = dummyCtx.createLinearGradient(10, 10, 300, 10);
    gradient.addColorStop(0, "#28C9DC");
    gradient.addColorStop(1, "#7B49C1");

    const wavesurfer = WaveSurfer.create({
      container: containerRef.current,
      waveColor: gradient as any,
      progressColor: gradient as any,
      cursorWidth: 0,
      height,
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      hideScrollbar: true,
      interact: false,
    });

    const recordPlugin = RecordPlugin.create({
      renderRecordedAudio: false,
      scrollingWaveform: true,
      continuousWaveform: false,
      continuousWaveformDuration: 200,
    });

    wavesurfer.registerPlugin(recordPlugin);

    const startRecording = async () => {
      if ((recordPlugin as any).isRecording?.()) {
        (recordPlugin as any).stopRecording?.();
      }
      await (recordPlugin as any).startRecording?.();
    };

    startRecording()
      .then(() => {
        const stream = (recordPlugin as any).stream;
        if (stream instanceof MediaStream) {
          visualizerMicStreams.add(stream);
        }
      })
      .catch((err: unknown) => {
        console.error("Could not start recording:", err);
      });

    return () => {
      releaseRecordPlugin(recordPlugin, wavesurfer);
    };
  }, [isRecording, height]);

  return (
    <Box
      ref={containerRef}
      sx={waveBoxSx(height, borderRadiusPx ?? Math.round(height / 2))}
    />
  );
};

// 2) AI — WaveSurfer rendered from peaks, refreshed while speaking (same look)
export const WaveSurferPeaksVisualizer: React.FC<{
  peaksRef: React.MutableRefObject<Float32Array>;
  isActive: boolean;
  lastPcmTick: number;
  height?: number;
  borderRadiusPx?: number;
}> = ({ peaksRef, isActive, lastPcmTick, height = 140, borderRadiusPx }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const silentUrlRef = useRef<string | null>(null);
  const lastUpdateRef = useRef<number>(0);

  const peaksToNumbers = useCallback(
    (peaks: Float32Array): number[] => Array.from(peaks, (v) => clamp01(v)),
    []
  );

  useEffect(() => {
    if (!containerRef.current) return;

    const dummyCanvas = document.createElement("canvas");
    const dummyCtx = dummyCanvas.getContext("2d");
    if (!dummyCtx) return;

    const gradient = dummyCtx.createLinearGradient(10, 10, 300, 10);
    gradient.addColorStop(0, "#28C9DC");
    gradient.addColorStop(1, "#7B49C1");

    const wavesurfer = WaveSurfer.create({
      container: containerRef.current,
      waveColor: gradient as any,
      progressColor: gradient as any,
      cursorWidth: 0,
      height,
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      hideScrollbar: true,
      interact: false,
    });

    waveSurferRef.current = wavesurfer;
    silentUrlRef.current = createSilentWavUrl();

    try {
      wavesurfer.load(
        silentUrlRef.current,
        [peaksToNumbers(peaksRef.current)],
        1
      );
    } catch {}

    return () => {
      try {
        if (silentUrlRef.current) URL.revokeObjectURL(silentUrlRef.current);
      } catch {}
      silentUrlRef.current = null;
      wavesurfer.destroy();
      waveSurferRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!waveSurferRef.current || !silentUrlRef.current) return;

    const ws = waveSurferRef.current;

    const tick = () => {
      const now = Date.now();
      const age = now - lastUpdateRef.current;

      if (age > 200) {
        const p = peaksRef.current;
        for (let i = 0; i < p.length; i++) {
          p[i] = Math.max(0.03, p[i] * 0.92);
        }
      }

      try {
        ws.load(silentUrlRef.current!, [peaksToNumbers(peaksRef.current)], 1);
      } catch {}
    };

    const interval = window.setInterval(tick, isActive ? 120 : 350);
    return () => window.clearInterval(interval);
  }, [isActive, peaksRef, peaksToNumbers]);

  useEffect(() => {
    lastUpdateRef.current = Date.now();
  }, [lastPcmTick]);

  return (
    <Box
      ref={containerRef}
      sx={waveBoxSx(height, borderRadiusPx ?? Math.round(height / 2))}
    />
  );
};

// 3) AI — Live visualization using getFrequencies() from WavStreamPlayer (same as user)
export const WaveSurferAiVisualizer: React.FC<{
  playerRef: React.MutableRefObject<WavStreamPlayer | null>;
  isActive: boolean;
  height?: number;
  borderRadiusPx?: number;
}> = ({ playerRef, isActive, height = 140, borderRadiusPx }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const silentUrlRef = useRef<string | null>(null);
  const peaksRef = useRef<number[]>([]);

  useEffect(() => {
    if (!containerRef.current) return;

    const dummyCanvas = document.createElement("canvas");
    const dummyCtx = dummyCanvas.getContext("2d");
    if (!dummyCtx) return;

    const gradient = dummyCtx.createLinearGradient(10, 10, 300, 10);
    gradient.addColorStop(0, "#28C9DC");
    gradient.addColorStop(1, "#7B49C1");

    const wavesurfer = WaveSurfer.create({
      container: containerRef.current,
      waveColor: gradient as any,
      progressColor: gradient as any,
      cursorWidth: 0,
      height,
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      hideScrollbar: true,
      interact: false,
    });

    waveSurferRef.current = wavesurfer;
    silentUrlRef.current = createSilentWavUrl();
    peaksRef.current = new Array(200).fill(0.03);

    try {
      wavesurfer.load(silentUrlRef.current, [peaksRef.current], 1);
    } catch {}

    return () => {
      try {
        if (silentUrlRef.current) URL.revokeObjectURL(silentUrlRef.current);
      } catch {}
      silentUrlRef.current = null;
      wavesurfer.destroy();
      waveSurferRef.current = null;
    };
  }, [height]);

  useEffect(() => {
    if (!waveSurferRef.current || !silentUrlRef.current) return;
    const ws = waveSurferRef.current;
    const updatePeaks = () => {
      try {
        const player = playerRef.current;
        if (!player) {
          for (let i = 0; i < peaksRef.current.length; i++) {
            peaksRef.current[i] = Math.max(0.03, peaksRef.current[i] * 0.92);
          }
        } else {
          try {
            const freqData = player.getFrequencies("frequency", -100, -30);
            if (freqData && freqData.values && freqData.values.length > 0) {
              const values = freqData.values;
              const numBars = peaksRef.current.length;
              const step = Math.max(1, Math.floor(values.length / numBars));

              let globalMax = 0;
              for (let i = 0; i < numBars; i++) {
                const start = i * step;
                const end = Math.min(values.length, start + step);
                let maxVal = 0;
                for (let j = start; j < end; j++) {
                  const val = Math.abs(values[j] || 0);
                  if (val > maxVal) maxVal = val;
                }
                if (maxVal > globalMax) globalMax = maxVal;
                const boosted = Math.min(1, maxVal * 1.5);
                peaksRef.current[i] = Math.max(
                  0.03,
                  peaksRef.current[i] * 0.6 + boosted * 0.4
                );
              }

              // If UI thinks AI is inactive but there's still audible energy,
              // keep the waveform alive (prevents "mute while AI still speaks" from freezing waves).
              const hasEnergy = globalMax > 0.02;
              if (!isActive && !hasEnergy) {
                for (let i = 0; i < peaksRef.current.length; i++) {
                  peaksRef.current[i] = Math.max(
                    0.03,
                    peaksRef.current[i] * 0.88
                  );
                }
              }
            } else {
              for (let i = 0; i < peaksRef.current.length; i++) {
                peaksRef.current[i] = Math.max(
                  0.03,
                  peaksRef.current[i] * 0.92
                );
              }
            }
          } catch (e) {
            console.warn("Error getting frequencies:", e);
            for (let i = 0; i < peaksRef.current.length; i++) {
              peaksRef.current[i] = Math.max(0.03, peaksRef.current[i] * 0.92);
            }
          }
        }

        try {
          ws.load(silentUrlRef.current!, [peaksRef.current], 1);
        } catch {}
      } catch (e) {
        // Ignore errors
      }
    };

    const interval = window.setInterval(updatePeaks, isActive ? 120 : 240);
    return () => window.clearInterval(interval);
  }, [isActive, playerRef]);

  return (
    <Box
      ref={containerRef}
      sx={waveBoxSx(height, borderRadiusPx ?? Math.round(height / 2))}
    />
  );
};
